import { RefCategory, useReferenceDataState } from "../../globalStates/ReferenceDataState"
import TreeCheck, { TreeData, TreeItemData } from "../../components/TreeCheck"
import { addElement, removeElementById } from "../../globalStates/utils"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLanguageState } from "../../globalStates/LanguageState"
import { Category } from "../../globalStates/ExhibitorDataState"
import { isNullOrEmpty } from "../../utils/Arrays"
import SearchField from "../../components/Search"
import { State } from "@hookstate/core"

function selectAllParents(node: TreeItemData) {
    if (node.parent) {
        node.parent.checked = true
        selectAllParents(node.parent)
    }
}

function makeNode(refCategory: RefCategory, subNodes: TreeItemData[], selectedLicences: Map<string, boolean>): TreeItemData {
    return {
        id: refCategory.id || "",
        rootId: "",
        text: refCategory.name || "",
        code: refCategory.code || "",
        selected: !!refCategory.id && !!selectedLicences.get(refCategory.id),
        checked: false,
        relevant: false,
        parent: null,
        nodes: subNodes
    }

}

function mapAllLicences2Nodes(
    parent: TreeItemData,
    refCategory: RefCategory[],
    selectedLicences: Map<string, boolean>
): TreeItemData[] {
    return refCategory.map((cat) => {
        const node = makeNode(cat, [], selectedLicences)
        node.parent = parent
        if (node.selected) selectAllParents(node)
        if (cat.subCategories) {
            node.nodes = mapAllLicences2Nodes(node, cat.subCategories, selectedLicences)
        }
        return node
    })
}

function isEligible(refCategory: RefCategory, filter: string) {
    const eligible = refCategory.name && refCategory.name.toLowerCase().indexOf(filter.toLowerCase()) >= 0

    return eligible
}

function mapLicence2Node(
    refCategory: RefCategory,
    filter: string,
    selectedLicences: Map<string, boolean>
) {
    let currentNode: TreeItemData | null = null

    if (isEligible(refCategory, filter)) {
        currentNode = makeNode(refCategory, [], selectedLicences)
        currentNode.relevant = !!filter
        if (refCategory.subCategories) {
            currentNode.nodes = mapAllLicences2Nodes(currentNode, refCategory.subCategories, selectedLicences)
        }

        return currentNode
    }

    if (!isNullOrEmpty(refCategory.subCategories)) {
        refCategory.subCategories?.forEach((refSubLicence) => {
            const subNode = mapLicence2Node(refSubLicence, filter, selectedLicences)
            if (subNode) {
                if (!currentNode) {
                    // create current node on demand
                    currentNode = makeNode(refCategory, [], selectedLicences)
                }
                subNode.parent = currentNode
                if (subNode.selected) selectAllParents(subNode)
                currentNode.nodes.push(subNode)
            }
        })
    }
    return currentNode
}

function mapLicences2Nodes(
    refCategory: RefCategory[] | null,
    filter: string,
    selectedLicences: Map<string, boolean>
): TreeData {
    const nodes: TreeItemData[] = []


    refCategory?.forEach((refCategory) => {
        const node = mapLicence2Node(refCategory, filter, selectedLicences)
        if (node) {
            nodes.push(node)
        }
    })
    return { nodes }
}

function selectedLicencesToMap(selLicences: Category[] | null): Map<string, boolean> {
    let selection = new Map()
    selLicences?.forEach((licence) => {
        selection.set(licence.id, true)
    })
    return selection
}

interface LicencesContainsProps {
    selectedLicencesStates: State<Category[]>
    refCategory: RefCategory[]
}

const LicencesContents: React.FC<LicencesContainsProps> = ({ selectedLicencesStates, refCategory }) => {
    const branding = useLanguageState().getStrings()
    const [filter, setFilter] = useState("")
    const refDataState = useReferenceDataState()
    const selected = useMemo(() => selectedLicencesToMap(selectedLicencesStates.get()), [selectedLicencesStates])
    const root = useMemo(() => mapLicences2Nodes(refCategory, filter, selected), [refCategory, filter, selected])

    useEffect(() => {
        if (!refDataState || !selected || selected.size === 0 || !refCategory || refCategory.length === 0) return
        selected.forEach((_, selectedId) => {
            const licence = refDataState.getCategory(selectedId)
            if (licence?.synthetic) removeElementById<Category>(selectedLicencesStates, selectedId)
        })
    }, [refCategory, selected, selectedLicencesStates]) // eslint-disable-line


    const onSelectionChangedHandler = useCallback(
        (id) => {
            const toRemoveCheck: boolean = selected.get(id) || false
            toRemoveCheck
                ? removeElementById<Category>(selectedLicencesStates, id)
                : addElement<Category>(selectedLicencesStates, { id })
        }, [selected, selectedLicencesStates]
    )

    const onChangeSearchHandler = useCallback((searchFilter) => {
        setFilter(searchFilter)
    }, [])

    return (
        <>
            <SearchField onChange={onChangeSearchHandler} />
            <TreeCheck showCode={branding.configuration.showLicencesCode} rootNode={root} routeNodesExpanded={true} selection={selected} onSelectionChanged={onSelectionChangedHandler} />
        </>
    )
}

export default LicencesContents